/**
 * This function escapes special characters in a stirng, so a search string
 * can be used to search through test for matches i.e. new RegExp(text, 'ig').test(data content)
 * This is nescessary because if you don't escape special characters in a RegExp, React
 * will crash if the user enters a special character in the search field.
 *
 * @param {string} text - The input search string.
 *
 * @return {string} Search str, w/ all special characters escaped to use in new RegExp().
 *
 * @example
 *
 *     const searchRegExp = new RegExp(escapeRegExp(value), 'ig');
 */
export function escapeRegExp(text) {
  return text.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
}

/**
 * This tells you if a string contains characters other than spaces, tabs, or line breaks.
 *
 * @param {string} str - Original string.
 *
 * @return {boolean} Whether the string contains only spaces, tabs, or line breaks - or NOT!
 *
 * @example
 *
 *     const search = value.length < 1 || strContainsOnlySpacesTabsOrLineBreaks(value);
 */
export function strContainsOnlySpacesTabsOrLineBreaks(str) {
  return !str.replace(/\s/g, '').length;
}

/**
 * Tells you whether or not a thingy is an object.
 *
 * @param {object} thingy - A thing we think might be an object.
 *
 * @return {boolean} Is the input thingy an object?
 *
 * @example
 *
 *     foo('hello')
 */
export function isObject(thingy) {
  return thingy != null && typeof thingy === 'object';
}

/**
 * Tells you whether 2 input objects are deep equal.
 *
 * @param {object} object1 - First object.
 * @param {object} object2 - Second object.
 *
 * @return {boolean} Are the 2 input objects deep equal?
 *
 * @example
 *
 *     areDeepEqual(object1, object2)
 */
export function areDeepEqual(object1, object2) {
  const keys1 = Object.keys(object1);
  const keys2 = Object.keys(object2);

  if (keys1.length !== keys2.length) {
    return false;
  }

  /* eslint-disable-next-line no-restricted-syntax */
  for (const key of keys1) {
    const val1 = object1[key];
    const val2 = object2[key];
    const areObjects = isObject(val1) && isObject(val2);
    if (
      (areObjects && !areDeepEqual(val1, val2))
      || (!areObjects && val1 !== val2)
    ) {
      return false;
    }
  }

  return true;
}

export function camelize(str) {
  return str.replace(/(?:^\w|[A-Z]|\b\w)/g, (word, index) => (index === 0 ? word.toLowerCase() : word.toUpperCase())).replace(/\s+/g, '');
}

export function toTitleCase(str) {
  return str.replace(
    /\w\S*/g,
    function(txt) {
      return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
    }
  );
}

export default {
  escapeRegExp,
  strContainsOnlySpacesTabsOrLineBreaks,
  isObject,
  areDeepEqual,
  camelize,
  toTitleCase,
};
